import * as React from "react"
import { fetchSetupIntentClientSecret, loadStripeJs } from "../../common/Stripe"
import * as _ from "lodash"

type Props = {
  onReady?: (ready: boolean) => void
  onLoadError?: (message: string) => void
  onChange?: (complete: boolean) => void
  memberId?: number
  chapterId?: number
}

/**
 * Stripe Elements card input.
 *
 * Card details are entered inside an iframe served by Stripe, so no card data
 * ever touches this application's DOM or servers. This is why the Accept.js
 * fields cannot simply be reused: Stripe only permits raw card data through
 * their API under PCI SAQ-D.
 *
 * The parent keeps a ref and calls createPaymentMethod() on submit.
 */
export default class StripeCardInput extends React.Component<Props> {
  private mountRef = React.createRef<HTMLDivElement>()
  private stripe: any
  private elements: any
  private card: any
  private isMounted_ = false

  async componentDidMount () {
    this.isMounted_ = true

    try {
      this.stripe = await loadStripeJs()

      if (!this.isMounted_ || !this.mountRef.current) {
        return
      }

      this.elements = this.stripe.elements()
      this.card = this.elements.create('card', {
        // The AuthNet flow never collected a ZIP code either; showing this
        // field here left it as an easy-to-miss required input that silently
        // kept the Card Element's `complete` flag false forever.
        hidePostalCode: true,
        style: {
          base: {
            fontSize: '16px',
            color: '#495057',
            fontFamily: 'Poppins, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
            '::placeholder': { color: '#adb5bd' },
          },
          invalid: { color: '#dc3545' },
        },
      })

      this.card.on('change', (event: any) => {
        if (this.props.onChange) {
          this.props.onChange(!!event.complete)
        }
      })

      this.card.mount(this.mountRef.current)

      if (this.props.onReady) {
        this.props.onReady(true)
      }
    } catch (e) {
      if (this.props.onLoadError) {
        this.props.onLoadError('There was an error contacting the payment provider')
      }
    }
  }

  componentWillUnmount () {
    this.isMounted_ = false

    if (this.card) {
      try {
        this.card.unmount()
        this.card.destroy()
      } catch (e) {
        // element may already be gone
      }
    }
  }

  /**
   * Tokenize whatever is currently in the card element.
   */
  async createPaymentMethod (billingName?: string): Promise<{ id: string, description: string }> {
    if (!this.stripe || !this.card) {
      throw new Error('The payment form is still loading, please try again')
    }

    const payload: any = { type: 'card', card: this.card }

    if (billingName) {
      payload.billing_details = { name: billingName }
    }

    const result = await this.stripe.createPaymentMethod(payload)

    if (result.error) {
      throw new Error(result.error.message || 'Your card could not be verified')
    }

    // Confirming a SetupIntent runs 3DS now if the issuer asks for it, rather
    // than letting an unauthenticated card fail its first renewal months from
    // now with nobody present to answer the challenge.
    const clientSecret = await fetchSetupIntentClientSecret(this.props.memberId, this.props.chapterId)
    const setupResult = await this.stripe.confirmCardSetup(clientSecret, { payment_method: result.paymentMethod.id })

    if (setupResult.error) {
      throw new Error(setupResult.error.message || 'Your card could not be verified')
    }

    const card = result.paymentMethod.card

    return {
      id: result.paymentMethod.id,
      description: card ? `${_.upperFirst(card.brand)} ending in XXXX${card.last4}` : 'Card',
    }
  }

  render () {
    return (
      <div
        className="form-control stripe-card-input"
        ref={this.mountRef}
        style={{ height: 'auto', paddingTop: 10, paddingBottom: 10 }}
      />
    )
  }
}
